// Browser-local store for SANDBOX orders and their test receipts.
//
// There is no server-side order record: fulfilment does not exist, and a
// server stub that minted credits without a verified payment is the one thing
// in this feature that could lose real money. So the sandbox flow keeps its
// state here, behind an interface a real API can replace wholesale.
//
// Two deliberate shape choices:
//
//  1. USER-SCOPED KEYS. AuthContext does not clear ordinary app localStorage on
//     sign-out, so a single shared key would show one Meta user's receipts to
//     the next person on a shared browser.
//
//  2. ONE KEY PER ORDER, not one key holding an array. An array forces
//     read-modify-write, and two tabs placing different orders would clobber
//     each other — deterministic ids only dedupe the SAME order, they don't fix
//     concurrent writes to different ones. Disjoint keys cannot interfere, so
//     no lock and no merge step is needed.
//
// Every read is shape-validated and every access is wrapped: localStorage
// throws in private-mode Safari, can be disabled outright, and can be full.
// A storage failure must degrade the sandbox, never break the page.

import { isApiRecord, type ApiValue } from '../../api/responseDecoders'
import type { Money, SandboxOrder } from './billingTypes'

const PREFIX = 'dw.billing-sandbox.v1'

function keyFor(userId: string, orderId: string): string {
  return `${PREFIX}.${userId}.order.${orderId}`
}

function prefixFor(userId: string): string {
  return `${PREFIX}.${userId}.order.`
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch {
    return null
  }
}

function isMoney(value: ApiValue): value is Money {
  if (!isApiRecord(value)) return false
  return (
    typeof value.amountMinor === 'number' &&
    Number.isFinite(value.amountMinor) &&
    typeof value.currency === 'string'
  )
}

/** Parse + validate one stored record. Returns null for anything malformed. */
function parseOrder(raw: string | null): SandboxOrder | null {
  if (raw === null) return null
  let value: ApiValue
  try {
    value = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isApiRecord(value)) return null
  const { orderId, packId, credits, price, createdAt, status, paidAt } = value
  if (
    typeof orderId !== 'string' ||
    typeof packId !== 'string' ||
    typeof credits !== 'number' ||
    !isMoney(price) ||
    typeof createdAt !== 'string' ||
    (status !== 'pending' && status !== 'paid')
  ) {
    return null
  }
  return {
    orderId,
    packId,
    credits,
    price,
    createdAt,
    status,
    paidAt: typeof paidAt === 'string' ? paidAt : null,
  }
}

export type WriteResult = { ok: true } | { ok: false; reason: 'unavailable' | 'failed' }

export function saveOrder(userId: string, order: SandboxOrder): WriteResult {
  const store = storage()
  if (store === null) return { ok: false, reason: 'unavailable' }
  try {
    store.setItem(keyFor(userId, order.orderId), JSON.stringify(order))
    return { ok: true }
  } catch {
    // Quota exceeded, or storage disabled mid-session. Report it rather than
    // pretending the order was recorded.
    return { ok: false, reason: 'failed' }
  }
}

export function getOrder(userId: string, orderId: string): SandboxOrder | null {
  const store = storage()
  if (store === null) return null
  try {
    return parseOrder(store.getItem(keyFor(userId, orderId)))
  } catch {
    return null
  }
}

/** All of this user's orders, newest first. Malformed records are dropped. */
export function listOrders(userId: string): SandboxOrder[] {
  const store = storage()
  if (store === null) return []
  const prefix = prefixFor(userId)
  const orders: SandboxOrder[] = []
  try {
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i)
      if (key === null || !key.startsWith(prefix)) continue
      const order = parseOrder(store.getItem(key))
      if (order !== null) orders.push(order)
    }
  } catch {
    return []
  }
  return orders.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

/** Outcome of a `markPaid` attempt. */
export type MarkPaidResult =
  | { ok: true; order: SandboxOrder }
  | { ok: false; reason: 'not_found' | 'unavailable' | 'failed' }

/**
 * Mark an order paid. Idempotent — replaying a placed order returns the
 * existing record rather than moving `paidAt`, so a double-submit or a
 * refreshed return URL converges instead of writing twice.
 *
 * Returns a RESULT rather than the order, because the write can fail and the
 * caller must not treat that as success: previously a failed `setItem` still
 * returned a paid-looking object, the checkout navigated to the success
 * screen, and a reload restored the still-pending order — the user had been
 * told the order was placed when nothing had been recorded.
 */
export function markPaid(userId: string, orderId: string, now = new Date()): MarkPaidResult {
  const existing = getOrder(userId, orderId)
  if (existing === null) return { ok: false, reason: 'not_found' }
  if (existing.status === 'paid') return { ok: true, order: existing }
  const paid: SandboxOrder = { ...existing, status: 'paid', paidAt: now.toISOString() }
  const written = saveOrder(userId, paid)
  if (!written.ok) return { ok: false, reason: written.reason }
  return { ok: true, order: paid }
}

/** Subscribe to cross-tab changes for this user. Returns an unsubscribe fn. */
export function subscribeToOrders(userId: string, onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  const prefix = prefixFor(userId)
  const handler = (event: StorageEvent) => {
    // `key === null` is a storage.clear() from another tab.
    if (event.key === null || event.key.startsWith(prefix)) onChange()
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/** Test seam. */
export function clearOrders(userId: string): void {
  const store = storage()
  if (store === null) return
  const prefix = prefixFor(userId)
  try {
    const doomed: string[] = []
    for (let i = 0; i < store.length; i += 1) {
      const key = store.key(i)
      if (key !== null && key.startsWith(prefix)) doomed.push(key)
    }
    doomed.forEach((key) => store.removeItem(key))
  } catch {
    /* nothing to do — a failed cleanup is not worth breaking a caller over */
  }
}
